import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
	CCard, CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody,
	CRow, CCol, CFormLabel, CFormInput, CButton, CBadge,
} from '@coreui/react';
import { DataGrid } from '@mui/x-data-grid';
import AxiosInstance from 'src/components/Axios';
import { STATUS_COLORS, STATUS_LABELS, SESSION_STATUS_COLORS, SESSION_STATUS_LABELS, SENDER_TYPE_COLORS, SENDER_TYPE_LABELS, formatDateTime } from 'src/constants/supportLetters';
import { getCachedSupportHistory, setCachedSupportHistory } from 'src/utils/supportRequestCache';

const urlApiletters = '/support-letters';
const HISTORY_PAGE_SIZE = 10;

const SupportRequestHistoryEmail = ({ userEmail, currentId, onOpenModal, patchedRow }) => {
	const [historyRows, setHistoryRows] = useState([]);
	const [historyTotal, setHistoryTotal] = useState(0);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [historyPage, setHistoryPage] = useState(0);
	const [historyDateFrom, setHistoryDateFrom] = useState('');
	const [historyDateTo, setHistoryDateTo] = useState('');
	const [historySessionId, setHistorySessionId] = useState('');
	const [historySessionSubject, setHistorySessionSubject] = useState('');

	const buildHistoryParams = () => {
		const params = {};
		if (historyDateFrom) params.date_from = historyDateFrom;
		if (historyDateTo) params.date_to = historyDateTo;
		if (historySessionId.trim()) params.session_id = historySessionId.trim();
		if (historySessionSubject.trim()) params.session_subject = historySessionSubject.trim();
		return params;
	};

	// Завантажує сторінку історії з бекенду; в кеш пише лише дефолтний перегляд (сторінка 1, без фільтрів)
	const fetchHistory = (email, page, params, silent = false) => {
		if (!silent) setHistoryLoading(true);
		AxiosInstance.get(`/support-requests/history/${encodeURIComponent(email)}`, { params: { page, limit: HISTORY_PAGE_SIZE, ...params } })
			.then(res => {
				const rows = res.data.data ?? [];
				const total = res.data.total ?? 0;
				setHistoryRows(rows);
				setHistoryTotal(total);
				const isDefaultView = page === 1 && !params.date_from && !params.date_to && !params.session_id && !params.session_subject;
				if (isDefaultView) setCachedSupportHistory(email, { data: rows, total, page, limit: HISTORY_PAGE_SIZE });
			})
			.catch(() => { })
			.finally(() => { if (!silent) setHistoryLoading(false); });
	};

	const handleHistorySearch = () => {
		setHistoryPage(0);
		fetchHistory(userEmail, 1, buildHistoryParams());
	};

	const handleHistoryClear = () => {
		setHistoryDateFrom('');
		setHistoryDateTo('');
		setHistorySessionId('');
		setHistorySessionSubject('');
		setHistoryPage(0);
		fetchHistory(userEmail, 1, {});
	};

	const handleHistoryPageChange = (newPage) => {
		setHistoryPage(newPage);
		fetchHistory(userEmail, newPage + 1, buildHistoryParams());
	};

	// Скидання фільтрів і завантаження історії (з кешу або з бекенду) при зміні користувача
	useEffect(() => {
		if (!userEmail) return;

		setHistoryDateFrom('');
		setHistoryDateTo('');
		setHistorySessionId('');
		setHistorySessionSubject('');
		setHistoryPage(0);

		const cachedHistory = getCachedSupportHistory(userEmail);
		if (cachedHistory) {
			setHistoryRows(cachedHistory.data || []);
			setHistoryTotal(cachedHistory.total ?? (cachedHistory.data || []).length);
		} else {
			fetchHistory(userEmail, 1, {});
		}
	}, [userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

	// Патчинг статусу конкретного рядка — і поточного листа при завантаженні, і листа, закритого в модалці
	useEffect(() => {
		if (!patchedRow || !userEmail) return;

		setHistoryRows(prev => prev.map(r =>
			r.id === patchedRow.id ? { ...r, status: patchedRow.status } : r
		));

		const cachedHistory = getCachedSupportHistory(userEmail);
		if (cachedHistory) {
			const patchedCacheRows = (cachedHistory.data || []).map(r =>
				r.id === patchedRow.id ? { ...r, status: patchedRow.status } : r
			);
			setCachedSupportHistory(userEmail, { ...cachedHistory, data: patchedCacheRows });
		}
	}, [patchedRow, userEmail]);

	if (!userEmail) return null;

	// ── Колонки: історія користувача ──
	const historyColumns = [
		{ field: 'id', headerName: 'ID', width: 60 },
		{
			field: 'session_id', headerName: 'Сесія', width: 100,
			renderCell: (params) => `Сесія №${params.row.session_id}`,
		},
		{ field: 'subject', headerName: 'Тема сесії та листа', flex: 1 },
		{
			field: 'session_status', headerName: 'Статус сесії (теми)', width: 180,
			renderCell: (params) => (
				<CBadge color={SESSION_STATUS_COLORS[params.row.session_status] || 'secondary'}>
					{SESSION_STATUS_LABELS[params.row.session_status] || params.row.session_status || '—'}
				</CBadge>
			),
		},
		{
			field: 'status', headerName: 'Статус листа', width: 140,
			renderCell: (params) => (
				<CBadge color={STATUS_COLORS[params.row.status] || 'secondary'}>
					{STATUS_LABELS[params.row.status] || params.row.status || '—'}
				</CBadge>
			),
		},
		{
			field: 'sender_type', headerName: 'Від', width: 70,
			renderCell: (params) => (
				<CBadge color={SENDER_TYPE_COLORS[params.row.sender_type] || 'secondary'}>
					{SENDER_TYPE_LABELS[params.row.sender_type] || params.row.sender_type || '—'}
				</CBadge>
			),
		},
		{
			field: 'created_at', headerName: 'Дата', width: 130,
			renderCell: (params) => formatDateTime(params.row.created_at),
		},
		{
			field: 'action_modal', headerName: '👁', headerAlign: 'center', align: 'center', sortable: false, width: 60,
			renderCell: (params) => (
				<button
					className="custom-view-icon"
					title={String(params.row.id) === currentId ? 'Цей лист вже відкрито' : 'Швидкий перегляд'}
					onClick={() => onOpenModal(params.row.id)}
					disabled={String(params.row.id) === currentId}
					style={{ background: 'none', cursor: String(params.row.id) === currentId ? 'default' : 'pointer', opacity: String(params.row.id) === currentId ? 0.3 : 1 }}
				>
					👁
				</button>
			),
		},
		{
			field: 'action', headerName: '', headerAlign: 'center', align: 'center', sortable: false, width: 50,
			renderCell: (params) => (
				String(params.row.id) === currentId
					? <span className="custom-view-icon" title="Цей лист вже відкрито" style={{ opacity: 0.3 }}>🔍</span>
					: <Link to={`${urlApiletters}/view/${params.row.id}`} className="custom-view-icon" title="Переглянути лист">
						🔍
					</Link>
			),
		},
	];

	return (
		<CCard className="mb-4">
			<CAccordion activeItemKey={1}>
				<CAccordionItem itemKey={1}>
					<CAccordionHeader>
						<strong>Історія користувача ({userEmail})</strong>
					</CAccordionHeader>
					<CAccordionBody className="p-3">
						<CAccordion flush className="mb-3">
							<CAccordionItem>
								<CAccordionHeader><strong>Фільтр</strong></CAccordionHeader>
								<CAccordionBody>
									<CRow className="g-2 align-items-end">
										<CCol md={2}>
											<CFormLabel>Дата від</CFormLabel>
											<CFormInput
												type="date"
												value={historyDateFrom}
												onChange={e => setHistoryDateFrom(e.target.value)}
												title="Дата від"
											/>
										</CCol>
										<CCol md={2}>
											<CFormLabel>Дата до</CFormLabel>
											<CFormInput
												type="date"
												value={historyDateTo}
												onChange={e => setHistoryDateTo(e.target.value)}
												title="Дата до"
											/>
										</CCol>
										<CCol md={2}>
											<CFormLabel>№ сесії</CFormLabel>
											<CFormInput
												value={historySessionId}
												onChange={e => setHistorySessionId(e.target.value.replace(/\D/g, ''))}
												placeholder="№ сесії"
												title="Пошук по номеру сесії (лише цифри)"
												inputMode="numeric"
											/>
										</CCol>
										<CCol md={3}>
											<CFormLabel>Назва сесії</CFormLabel>
											<CFormInput
												value={historySessionSubject}
												onChange={e => setHistorySessionSubject(e.target.value)}
												placeholder="Назва сесії"
												title="Пошук по назві сесії"
											/>
										</CCol>
										<CCol md={3} className="d-flex gap-2 justify-content-end">
											<CButton color="primary" onClick={handleHistorySearch} title="Застосувати фільтри">
												Пошук
											</CButton>
											<CButton color="secondary" variant="outline" onClick={handleHistoryClear} title="Скинути фільтри">
												Очистити
											</CButton>
										</CCol>
									</CRow>
								</CAccordionBody>
							</CAccordionItem>
						</CAccordion>
						<div style={{ height: 300, width: '100%' }}>
							<DataGrid
								rows={historyRows}
								columns={historyColumns}
								page={historyPage}
								pageSize={HISTORY_PAGE_SIZE}
								rowsPerPageOptions={[HISTORY_PAGE_SIZE]}
								pagination
								paginationMode="server"
								rowCount={historyTotal}
								loading={historyLoading}
								onPageChange={handleHistoryPageChange}
								disableSelectionOnClick
								initialState={{
									sorting: { sortModel: [{ field: 'id', sort: 'desc' }] },
								}}
							/>
						</div>
					</CAccordionBody>
				</CAccordionItem>
			</CAccordion>
		</CCard>
	);
};

export default SupportRequestHistoryEmail;
